// Libray imports
import React, { useState } from 'react';
import { MDBIcon, MDBListGroupItem, MDBRow } from 'mdb-react-ui-kit';
import { useSelector } from 'react-redux';
import { bool, shape, string } from 'prop-types';
// Component imports
import PillNav from '../../../../components/PillNav';
// Utils imports
import { imageLoadError } from '../../../../utils';
import { calcImpliedOdds, getStarRating, getGameTeamData } from '../../utils';
import { formatOdd } from '../../../../utils/odds';
import { shortName } from '../../../../utils/strings';
import useScreenWidth from '../../../../utils/hooks/useScreenWidth';
// Constants
import { MOBILE_SCREEN_SIZE } from '../../../../utils/constants';
// Styled components
import {
    PowerContent,
    TeamSectionContainer,
    TeamLogo,
    TeamLogoWrap,
    TeamNameWrapper,
    PowerDataContainer,
    PowerItemContainer,
    PowerRankArrow,
    PowerItemLabel,
    PowerCol,
    CenterPowerCol,
    PowerCardTitle,
    PowerTeamName,
    PowerPillWrap,
    PowerField,
    PowerTeamId,
    PowerRankListGroup,
} from './styles';
import { TeamRank } from '../styles';

const PowerRank = ({ demo = false, game, sport, history = false }) => {
    // Redux
    const { teams } = useSelector(state => state.app);
    const { awayTeamData, homeTeamData } = getGameTeamData(teams, game, demo, sport);
    // Local state
    const [type, setType] = useState(sport === 'mlb' || sport === 'nhl' ? 'moneyline' : 'spread');
    // Hooks
    const screenWidth = useScreenWidth();
    const isMobile = screenWidth <= MOBILE_SCREEN_SIZE;
    const isLockedPrediction = Boolean(game?.predictionUnlock && !game.predictionUnlock.unlocked);

    const awayTeamVegasOdd = type === 'spread' ? game.awayTeam.odds?.fullGame?.spread : game.awayTeam.odds?.fullGame?.moneyline;
    const homeTeamVegasOdd = type === 'spread' ? game.homeTeam.odds?.fullGame?.spread : game.homeTeam.odds?.fullGame?.moneyline;

    const getPowerPredictionValue = team => {
        if (!game.prediction || isLockedPrediction) return null;
        const { awayScore, homeScore, powerMoneyline } = game.prediction;
        const hasAwayScore = Number.isFinite(Number(awayScore));
        const hasHomeScore = Number.isFinite(Number(homeScore));
        if (!hasAwayScore || !hasHomeScore) {
            return null;
        }
        if (type === 'spread') {
            if (team === 'home') {
                return Number((awayScore - homeScore).toFixed(1));
            } else {
                return Number((homeScore - awayScore).toFixed(1));
            }
        } else {
            var oddsToFormat;
            if (team === 'home') {
                oddsToFormat = homeScore - awayScore > 0 ? powerMoneyline.favML : powerMoneyline.dogML;
            } else {
                oddsToFormat = awayScore - homeScore > 0 ? powerMoneyline.favML : powerMoneyline.dogML;
            }
            return Number(oddsToFormat);
        }
    };

    const getPowerPrediction = team => {
        const value = getPowerPredictionValue(team);
        if (value == null) return null;
        return formatOdd(value, type);
    };

    const calcSpreadDifference = (vegasSpread, powerSpread) => {
        if (!vegasSpread || isNaN(vegasSpread)) return 0;
        if (!vegasSpread || isNaN(powerSpread)) return 0;
        const difference = Number(vegasSpread - powerSpread).toFixed(2);
        return formatOdd(difference, 'spread');
    };

    const getSosClass = sos => {
        if (sos == null) return undefined;
        const SOS_MIDPOINTS = { nfl: 16, cfb: 65, nba: 15, cbb: 182, nhl: 16, mlb: 15 };
        const midpoint = SOS_MIDPOINTS[sport] || 16;
        return sos <= midpoint ? 'sos-good' : 'sos-bad';
    };

    const getHighlightVariant = team => {
        const powerPredictionValue = getPowerPredictionValue(team);
        if (type === 'moneyline') {
            if (powerPredictionValue == null || Number.isNaN(Number(powerPredictionValue))) {
                return 'light';
            }
            // If powerLineWinner is 0, no team has betting value - show neutral
            if (!game.prediction['powerLineWinner']) {
                return 'light';
            }
            if (game[`${team}Team`].teamId === game.prediction['powerLineWinner']) {
                return 'success';
            } else {
                return 'danger';
            }
        }
        if (type === 'spread') {
            if (!calcSpreadDifference(game[`${team}Team`].odds?.fullGame?.spread, Number(powerPredictionValue))) {
                return 'light';
            }
            if (game[`${team}Team`].teamId === game.prediction.spreadWinner) {
                return 'success';
            } else {
                return 'danger';
            }
        }
        return 'light';
    };

    const generateTeamWrapper = team => {
        var score = 0;
        if (!team) {
            console.log(`No team found for game ${game.awayTeam.teamName} vs ${game.homeTeam.teamName}`);
            return (
                <TeamSectionContainer>
                    <TeamNameWrapper side="left">
                        <PowerCardTitle color="#000" responsive={true} title="NA">
                            <PowerTeamName>NA</PowerTeamName>
                        </PowerCardTitle>
                    </TeamNameWrapper>
                </TeamSectionContainer>
            );
        }
        if (history) {
            score = team.teamId === game.awayTeam.teamId ? game.awayTeam.score : game.homeTeam.score;
        }
        const matchedFilter = game.awayTeam.teamId === team.teamId ? game.awayTeam.matchedFilter : game.homeTeam.matchedFilter;
        return (
            <TeamSectionContainer>
                <TeamNameWrapper side={game.awayTeam.teamId === team.teamId ? 'left' : 'right'}>
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                        <TeamLogoWrap>
                            <TeamLogo src={team.logoUrl} id={team.teamId} alt="team logo" onError={imageLoadError} />
                        </TeamLogoWrap>
                        {matchedFilter && (
                            <span
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: team.teamId === game.awayTeam.teamId ? '100%' : 'auto',
                                    right: team.teamId === game.homeTeam.teamId ? '100%' : 'auto',
                                    transform: 'translateY(-50%)',
                                    backgroundColor: '#28a745',
                                    color: 'white',
                                    fontSize: '0.6rem',
                                    padding: '1px 4px',
                                    borderRadius: '8px',
                                    fontWeight: 'bold',
                                    whiteSpace: 'nowrap',
                                    zIndex: 10,
                                    marginLeft: team.teamId === game.awayTeam.teamId ? '2px' : '0',
                                    marginRight: team.teamId === game.homeTeam.teamId ? '2px' : '0',
                                }}
                            >
                                MATCH
                            </span>
                        )}
                    </div>
                    <PowerCardTitle color={team.primaryColor} responsive={true} title={team.teamName}>
                        {team.rank && <TeamRank right={String(team.teamId !== game.homeTeam.teamId)}>#{team.rank}</TeamRank>}
                        <PowerTeamName>{shortName(team, isMobile ? 10 : 15)}</PowerTeamName>
                    </PowerCardTitle>
                    <PowerTeamId>({team.abbr})</PowerTeamId>
                </TeamNameWrapper>
                <PowerRankListGroup coloredList={history} silverBg="true" direction="row" responsive={true} rightJustify={team.teamId === game.homeTeam.teamId}>
                    {history ? (
                        <>
                            <MDBListGroupItem color={team.teamId === game.winner ? 'success' : 'danger'}>
                                <label>{team.teamId === game.winner ? 'Winner' : 'Loser'}</label>
                                <span>{score}</span>
                            </MDBListGroupItem>
                            <MDBListGroupItem color={team.teamId === game.spreadWinner ? 'success' : 'danger'}>
                                <label>Spread</label>
                                <span>{team.teamId === game.spreadWinner ? <MDBIcon icon="check" /> : <MDBIcon icon="times" />}</span>
                            </MDBListGroupItem>
                            <MDBListGroupItem
                                color={
                                    game.prediction.powerLineWinner
                                        ? team.teamId === game.prediction.powerLineWinner && team.teamId === game.winner
                                            ? 'success'
                                            : 'danger'
                                        : 'light'
                                }
                            >
                                <label>Proven Line</label>
                                <span>
                                    {!game.prediction.powerLineWinner ? (
                                        <MDBIcon icon="equals" />
                                    ) : team.teamId === game.prediction.powerLineWinner && team.teamId === game.winner ? (
                                        <MDBIcon icon="check" />
                                    ) : (
                                        <MDBIcon icon="times" />
                                    )}
                                </span>
                            </MDBListGroupItem>
                            <MDBListGroupItem
                                color={
                                    game.prediction.spreadWinner
                                        ? team.teamId === game.prediction.spreadWinner && team.teamId === game.spreadWinner
                                            ? 'success'
                                            : 'danger'
                                        : 'light'
                                }
                            >
                                <label>Power Spread</label>
                                <span>
                                    {!game.prediction.spreadWinner ? (
                                        <MDBIcon icon="equals" />
                                    ) : team.teamId === game.prediction.spreadWinner && team.teamId === game.spreadWinner ? (
                                        <MDBIcon icon="check" />
                                    ) : (
                                        <MDBIcon icon="times" />
                                    )}
                                </span>
                            </MDBListGroupItem>
                        </>
                    ) : (
                        <>
                            <MDBListGroupItem>
                                <label>Record</label>
                                <span>{team.recordData ? team.recordData.record : '0-0'}</span>
                            </MDBListGroupItem>
                            <MDBListGroupItem>
                                <label>{team.teamId === game.awayTeam.teamId ? 'Away' : 'Home'}</label>
                                <span>
                                    {team.recordData?.awayRecord && team.recordData?.homeRecord
                                        ? `${team.teamId === game.awayTeam.teamId ? team.recordData.awayRecord : team.recordData.homeRecord}`
                                        : '0-0'}
                                </span>
                            </MDBListGroupItem>
                            <MDBListGroupItem>
                                <label>ATS</label>
                                <span>{team.recordData?.ats ? `${team.recordData.ats}` : '0-0'}</span>
                            </MDBListGroupItem>
                            <MDBListGroupItem className={getSosClass(game.awayTeam.teamId === team.teamId ? game.awayTeam.sos : game.homeTeam.sos)}>
                                <label>SOS Played</label>
                                <span title="Avg rank of opponents faced so far (lower = harder schedule)">
                                    {(game.awayTeam.teamId === team.teamId ? game.awayTeam.sos : game.homeTeam.sos) ?? 'N/A'}
                                </span>
                            </MDBListGroupItem>
                        </>
                    )}
                </PowerRankListGroup>
            </TeamSectionContainer>
        );
    };

    const renderStarRating = confidence => {
        if (isLockedPrediction) {
            return (
                <div style={{ fontSize: '1em', color: '#fcd34d', textAlign: 'center' }}>
                    <i className="fas fa-lock" style={{ marginRight: 6 }} />
                    <span style={{ fontWeight: 700 }}>PW Rating Locked</span>
                    <div style={{ fontSize: '0.75em', color: '#f8fafc' }}>Unlock prediction to view PW Rating</div>
                </div>
            );
        }
        const rating = getStarRating(confidence);
        const stars = Array.from({ length: 5 }, (_, i) => (i < rating ? '★' : '☆'));
        return (
            <div style={{ fontSize: '1em', color: '#FFD700', textAlign: 'center' }}>
                {stars.join(' ')}
                <div style={{ fontSize: '0.75em', color: '#888' }}>PW Rating: {Math.round((confidence || 0) * 100)}%</div>
            </div>
        );
    };

    return (
        <PowerContent style={{ overflow: 'hidden' }} fluid>
            <MDBRow className="gx-1 gx-md-4" style={{ flexWrap: 'nowrap' }}>
                <PowerCol>{generateTeamWrapper(awayTeamData)}</PowerCol>
                <CenterPowerCol>
                    <PowerDataContainer>
                        <PowerPillWrap>
                            <PillNav
                                tabs={['moneyline', 'spread']}
                                activeTab={type}
                                setActiveTab={setType}
                                buttonStyle={{ fontSize: '0.75rem' }}
                                containerStyle={{ minWidth: isMobile ? 0 : '10rem' }}
                            />
                        </PowerPillWrap>
                        <PowerItemContainer>
                            <PowerItemLabel>Consensus</PowerItemLabel>
                            <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-evenly' }}>
                                <PowerField
                                    variant={game.awayTeam.odds?.fullGame?.moneyline < game.homeTeam.odds?.fullGame?.moneyline ? 'primary' : 'light'}
                                    style={{ minWidth: isMobile ? '2.75rem' : '3.5rem' }}
                                >
                                    <span>{formatOdd(awayTeamVegasOdd, type)}</span>
                                    {type === 'spread' ? (
                                        <span style={{ display: 'block', fontSize: '12px' }}>Spread</span>
                                    ) : (
                                        <span style={{ display: 'block', fontSize: '12px' }}>{calcImpliedOdds(awayTeamVegasOdd)}%</span>
                                    )}
                                </PowerField>
                                {game.awayTeam.odds?.fullGame?.moneyline < game.homeTeam.odds?.fullGame?.moneyline ? (
                                    <PowerRankArrow icon="circle-arrow-left" />
                                ) : (
                                    <PowerRankArrow icon="circle-arrow-right" />
                                )}
                                <PowerField
                                    variant={game.awayTeam.odds?.fullGame?.moneyline > game.homeTeam.odds?.fullGame?.moneyline ? 'primary' : 'light'}
                                    style={{ minWidth: isMobile ? '2.75rem' : '3.5rem' }}
                                >
                                    <span>{formatOdd(homeTeamVegasOdd, type)}</span>
                                    {type === 'spread' ? (
                                        <span style={{ display: 'block', fontSize: '12px' }}>Spread</span>
                                    ) : (
                                        <span style={{ display: 'block', fontSize: '12px' }}>{calcImpliedOdds(homeTeamVegasOdd)}%</span>
                                    )}
                                </PowerField>
                            </div>
                        </PowerItemContainer>
                        <PowerItemContainer>
                            <PowerItemLabel style={{ marginBottom: 0 }}>Proven Line</PowerItemLabel>
                            {renderStarRating(type === 'spread' ? game.prediction?.spreadConfidence : game.prediction?.confidence)}
                            <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-evenly' }}>
                                <PowerField variant={getHighlightVariant('away')} style={{ minWidth: isMobile ? '2.75rem' : '3.5rem' }}>
                                    <span>
                                        {isLockedPrediction ? (
                                            <MDBIcon icon="lock" />
                                        ) : (
                                            getPowerPrediction('away') || 'NA'
                                        )}
                                    </span>
                                    {isLockedPrediction ? (
                                        <></>
                                    ) : type === 'spread' ? (
                                        <span style={{ display: 'block', fontSize: '12px' }}>
                                            {getPowerPredictionValue('away') == null
                                                ? '—'
                                                : calcSpreadDifference(game.awayTeam.odds?.fullGame?.spread, getPowerPredictionValue('away'))}
                                        </span>
                                    ) : (
                                        <span style={{ display: 'block', fontSize: '12px' }}>
                                            {getPowerPredictionValue('away') == null ? '—' : `${calcImpliedOdds(getPowerPredictionValue('away'))}%`}
                                        </span>
                                    )}
                                </PowerField>
                                {isLockedPrediction ? (
                                    <PowerRankArrow icon="lock" />
                                ) : type === 'spread' ? (
                                    game.awayTeam.teamId === game.prediction.spreadWinner ? (
                                        <PowerRankArrow icon="circle-arrow-left" />
                                    ) : (
                                        <PowerRankArrow icon="circle-arrow-right" />
                                    )
                                ) : !game.prediction['powerLineWinner'] ? (
                                    <PowerRankArrow icon="ban" />
                                ) : game.awayTeam.teamId === game.prediction['powerLineWinner'] ? (
                                    <PowerRankArrow icon="circle-arrow-left" />
                                ) : (
                                    <PowerRankArrow icon="circle-arrow-right" />
                                )}
                                <PowerField variant={getHighlightVariant('home')} style={{ minWidth: isMobile ? '2.75rem' : '3.5rem' }}>
                                    <span>
                                        {isLockedPrediction ? (
                                            <MDBIcon icon="lock" />
                                        ) : (
                                            getPowerPrediction('home') || 'NA'
                                        )}
                                    </span>
                                    {isLockedPrediction ? (
                                        <></>
                                    ) : type === 'spread' ? (
                                        <span style={{ display: 'block', fontSize: '12px' }}>
                                            {getPowerPredictionValue('home') == null
                                                ? '—'
                                                : calcSpreadDifference(game.homeTeam.odds?.fullGame?.spread, getPowerPredictionValue('home'))}
                                        </span>
                                    ) : (
                                        <span style={{ display: 'block', fontSize: '12px' }}>
                                            {getPowerPredictionValue('home') == null ? '—' : `${calcImpliedOdds(getPowerPredictionValue('home'))}%`}
                                        </span>
                                    )}
                                </PowerField>
                            </div>
                        </PowerItemContainer>
                    </PowerDataContainer>
                </CenterPowerCol>
                <PowerCol>{generateTeamWrapper(homeTeamData)}</PowerCol>
            </MDBRow>
        </PowerContent>
    );
};

PowerRank.propTypes = {
    demo: bool,
    game: shape({}).isRequired,
    sport: string.isRequired,
    history: bool,
};

export default PowerRank;
